<script setup lang="ts">
  import { useDebounceFn } from '@vueuse/core'
  import type { ComponentPublicInstance } from 'vue'
  import type { FareTypesStoryblok } from '@/storyblok/component-types-sb'
  import type { IRadioItem } from '@/components/ui/radio/types'
  import type { SwiperSettings } from '@/components/ui/slider/types'
  import type Swiper from 'swiper'
  import type { TFareCategory, TFareItem, SwiperComponentRef } from '@/storyblok/types'

  const props = defineProps<{
    blok: FareTypesStoryblok
  }>()

  const activeCategory = ref<TFareCategory>('economy')

  const { t } = useI18n()

  type FareTabItem = IRadioItem & { value: TFareCategory }

  const tabItems = computed<FareTabItem[]>(() => [
    { value: 'economy', label: t('fareTypes.economy') },
    { value: 'business', label: t('fareTypes.business') }
  ])

  const getFareTypeTabId = (value: TFareCategory) => `fare-type-tab-${props.blok._uid}-${value}`

  const fareTypeSliderSettings = computed<SwiperSettings>(() => ({
    speed: 500,
    keyboard: false,
    slidesPerView: 1,
    spaceBetween: '0',
    loop: false,
    watchSlidesProgress: true,
    slidesPerGroup: 1,
    autoHeight: true,
    pagination: {
      el: `#pagination-${props.blok._uid}`,
      clickable: true,
      bulletActiveClass: 'border-bt-blue-dark',
      renderBullet: (index: number, className: string) => {
        return `<span class="${className} w-8 lg:w-14 border-b-4 border-border block h-1 cursor-pointer"></span>`
      }
    }
  }))

  const { isTablet, isDesktop, isMobile } = useBTBreakpoints()

  const fareSlidesPerView = computed(() => {
    if (isDesktop.value) return 4
    if (isTablet.value) return 2
    return 1
  })

  const getFares = (category: TFareCategory) => {
    const fares = props.blok[category]

    if (!Array.isArray(fares)) return []

    return fares.filter(
      (fare): fare is TFareItem =>
        typeof fare !== 'string' && fare !== null && typeof fare === 'object' && 'content' in fare
    )
  }

  const formatFares = (fares: TFareItem[]) => {
    if (fares.length === 2 && isDesktop.value) {
      return [null, ...fares]
    }

    return fares
  }

  const shouldCenterSlides = computed(() => {
    const length = getFares(activeCategory.value).length

    return length === 1 && !isMobile.value
  })

  const fareSliderSettings = computed<SwiperSettings>(() => ({
    speed: 500,
    keyboard: true,
    slidesPerView: fareSlidesPerView.value,
    spaceBetween: '24px',
    loop: false,
    watchSlidesProgress: true,
    slidesPerGroup: fareSlidesPerView.value,
    nested: true,
    centeredSlides: shouldCenterSlides.value
  }))

  const swiperElement = ref<SwiperComponentRef | null>(null)
  const slideContentEls = new Map<TFareCategory, HTMLElement>()
  let activeSlideResizeObserver: ResizeObserver | null = null

  const innerSwiperElements = ref<Partial<Record<TFareCategory, SwiperComponentRef | null>>>({})

  const isSwiperComponentRef = (element: unknown): element is SwiperComponentRef => {
    return (
      typeof element === 'object' &&
      element !== null &&
      !(element instanceof Element) &&
      'swiper' in element &&
      'slideTo' in element
    )
  }

  const setInnerSwiperElement = (category: TFareCategory, element: Element | ComponentPublicInstance | null) => {
    innerSwiperElements.value[category] = isSwiperComponentRef(element) ? element : null
  }

  const refreshOuterSwiperHeight = async () => {
    await nextTick()

    const swiper = swiperElement.value?.swiper
    if (!swiper) return

    const updateAutoHeight = typeof swiper.updateAutoHeight === 'function' ? () => swiper.updateAutoHeight(0) : null
    const update = typeof swiper.update === 'function' ? () => swiper.update() : null

    if (!updateAutoHeight && !update) return

    const refresh = () => {
      updateAutoHeight?.()
      update?.()
    }

    requestAnimationFrame(() => {
      refresh()

      requestAnimationFrame(() => {
        refresh()
      })
    })

    globalThis.setTimeout(() => {
      refresh()
    }, 150)
  }

  const debouncedRefreshOuterSwiperHeight = useDebounceFn(() => {
    void refreshOuterSwiperHeight()
  }, 16)

  const observeActiveSlideContent = () => {
    activeSlideResizeObserver?.disconnect()
    activeSlideResizeObserver = null

    const activeContentEl = slideContentEls.get(activeCategory.value)
    if (!activeContentEl || typeof ResizeObserver === 'undefined') return

    activeSlideResizeObserver = new ResizeObserver(() => {
      debouncedRefreshOuterSwiperHeight()
    })
    activeSlideResizeObserver.observe(activeContentEl)
  }

  const setSlideContentRef = (category: TFareCategory, element: Element | ComponentPublicInstance | null) => {
    let rootEl: HTMLElement | null = null
    if (element instanceof HTMLElement) {
      rootEl = element
    } else if (element && '$el' in element && element.$el instanceof HTMLElement) {
      rootEl = element.$el
    }

    if (rootEl) {
      slideContentEls.set(category, rootEl)
      if (category === activeCategory.value) {
        observeActiveSlideContent()
      }
      return
    }

    slideContentEls.delete(category)
    if (category === activeCategory.value) {
      observeActiveSlideContent()
    }
  }

  const onActiveSlideIndexChange = (event: CustomEvent<Swiper[]>) => {
    const target = event.target as HTMLElement | null

    if (target?.id !== `fare-type-${props.blok._uid}`) return

    const nextValue = tabItems.value[event.detail[0].realIndex]?.value
    if (nextValue && nextValue !== activeCategory.value) {
      activeCategory.value = nextValue
    }
  }

  watch(activeCategory, async (value, oldValue) => {
    const index = tabItems.value.findIndex((item) => item.value === value)

    if (index !== -1 && swiperElement.value?.swiper?.realIndex !== index) {
      swiperElement.value?.slideTo(index)
    }

    observeActiveSlideContent()
    void refreshOuterSwiperHeight()

    if (oldValue && oldValue !== value) {
      await nextTick()

      const swiperComponent = innerSwiperElements.value[value]
      swiperComponent?.slideTo(0)
    }
  })

  onMounted(() => {
    observeActiveSlideContent()
    void refreshOuterSwiperHeight()
  })

  onBeforeUnmount(() => {
    activeSlideResizeObserver?.disconnect()
  })
</script>

<template>
  <RadioGroupRoot
    v-model="activeCategory"
    class="inline-flex w-full gap-1 rounded-full bg-grey p-2 sm:max-w-174"
    :aria-label="t('fareTypes.aria-label')"
  >
    <div
      v-for="(item, index) in tabItems"
      :key="`fare-tab-${index}`"
      class="flex-1 rounded-full"
    >
      <RadioGroupItem
        :id="getFareTypeTabId(item.value)"
        :value="item.value"
        :disabled="item.disabled"
        :aria-label="item.label"
        class="block w-full rounded-full py-2 text-center outline-none text-subtitle-2 data-[state=checked]:bg-white data-[state=checked]:text-bt-blue-dark data-[state=checked]:shadow-sm"
      >
        <span
          class="block cursor-pointer data-[disabled]:cursor-not-allowed"
          :class="{ 'cursor-default': item.disabled }"
        >
          {{ item.label }}
        </span>
      </RadioGroupItem>
    </div>
  </RadioGroupRoot>

  <div>
    <UiSliderComponent
      :id="`fare-type-${props.blok._uid}`"
      ref="swiperElement"
      :slides="tabItems"
      :slider-settings="fareTypeSliderSettings"
      @swiperactiveindexchange="onActiveSlideIndexChange"
    >
      <template #slide="{ slide }">
        <div :ref="(element) => setSlideContentRef(slide.value, element)">
          <LazyUiSliderComponent
            :ref="(element) => setInnerSwiperElement(slide.value, element)"
            :slides="formatFares(getFares(slide.value))"
            :slider-settings="fareSliderSettings"
            class="fare-slider"
          >
            <template #slide="{ slide: fare }">
              <FareTypesFare
                v-if="fare && typeof fare !== 'string'"
                :blok="fare"
                :category="slide.value"
              />
            </template>
          </LazyUiSliderComponent>
        </div>
      </template>

      <template #slider-container-end>
        <div
          v-if="props.blok._uid"
          :id="`pagination-${props.blok._uid}`"
          class="mt-8 flex h-5 justify-center gap-4 lg:mt-10"
        />
      </template>
    </UiSliderComponent>
  </div>
</template>
